import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  API_BASE_URL,
  ApiError,
  type Tournament,
  type TournamentFormatConfig,
  type TournamentRule,
} from "../lib/api";
import {
  SETUP_RULE_FIELDS,
  composeSetupRuleContent,
  emptySetupRuleSections,
  parseSetupRuleContent,
  type SetupRuleFieldKey,
  type SetupRuleSections,
} from "../lib/ruleSections";
import { absoluteAssetUrl, buildGeneratedRulesPresentation, tournamentDataRuleItems } from "../lib/rulesPresentation";
import FieldHint from "../components/FieldHint";
import WizUpload from "../components/WizUpload";
import { RULE_MAX_ATTACHMENTS, ruleAttachments, uploadedFileName } from "../lib/ruleAttachments";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import { formatDateTime } from "../lib/dateFormat";
import GuideHelpButton from "../components/GuideHelpButton";

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  published: "Publicado",
};

interface DraftFormState {
  title: string;
  content: string;
}

const EMPTY_FORM: DraftFormState = { title: "", content: "" };

interface Props {
  setupMode?: boolean;
}

function escapeHtml(value: string | number | null | undefined) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function linesToHtml(value: string | null | undefined) {
  return escapeHtml(value || "—").replaceAll("\n", "<br />");
}

export default function AdminTournamentRulesPage({ setupMode = false }: Props) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token } = useAuth();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [formatConfig, setFormatConfig] = useState<TournamentFormatConfig | null>(null);
  const [rules, setRules] = useState<TournamentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [draftForm, setDraftForm] = useState<DraftFormState>(EMPTY_FORM);

  const loadTournament = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
      setFormatConfig(response.data.format_config);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }, [token, tournamentId]);

  const loadRules = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.rules.list(token, tournamentId);
      setRules(response.data.rules);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el reglamento.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void loadTournament();
  }, [loadTournament]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const draft = useMemo(
    () => rules.find((r) => r.status === "draft") ?? null,
    [rules],
  );
  const publishedVersions = useMemo(
    () => rules.filter((r) => r.status === "published"),
    [rules],
  );
  const currentPublished = publishedVersions[0] ?? null;
  // Eduardo 2026-10-05 (torneo 69): los PDF propios viven en UNA versión
  // destino: el borrador si existe; si no, la publicada vigente (ahí sube
  // ensureDraftForPdfUpload). La lista, el tope de 3 y «Quitar» miran esa
  // misma versión; antes solo miraban el borrador y, con el reglamento
  // publicado, el campo salía vacío y habilitado aunque ya hubiera 3.
  const pdfTarget = draft ?? currentPublished;
  // Hasta 3 PDFs propios con su nombre visible (Eduardo 2026-09-10).
  const pdfAttachments = ruleAttachments(pdfTarget);
  const pdfLimitReached = pdfAttachments.length >= RULE_MAX_ATTACHMENTS;
  // Estado propio (no derivado por parseo en cada render): escribir en un
  // textarea no debe pasar por el ciclo parsear⇄componer, que recorta espacios
  // y pierde teclazos. `draftForm.content` se mantiene sincronizado al componer.
  const [setupRuleSections, setSetupRuleSections] = useState<SetupRuleSections>(emptySetupRuleSections());
  const generatedRules = useMemo(
    () => buildGeneratedRulesPresentation(tournament, formatConfig),
    [tournament, formatConfig],
  );

  // Eduardo 2026-10-05: el formulario se rellena solo cuando cambia la
  // versión de origen o su texto guardado. Subir o quitar un PDF reemplaza
  // la regla en `rules` (mismo id y mismo texto) y antes eso borraba lo que
  // el admin llevaba escrito sin guardar.
  const formSource = draft ?? currentPublished;
  const formSourceId = formSource?.id ?? null;
  const formSourceTitle = formSource?.title ?? "";
  const formSourceContent = formSource?.content ?? "";
  // Se ajusta durante el render (no en un efecto): el efecto corría después
  // de pintar y, si el admin escribía en ese instante, pisaba lo escrito con
  // el texto guardado (prueba intermitente en CI).
  const formSourceKey = formSourceId === null ? null : JSON.stringify([formSourceId, formSourceTitle, formSourceContent]);
  const [appliedFormSourceKey, setAppliedFormSourceKey] = useState<string | null>(null);
  if (appliedFormSourceKey !== formSourceKey) {
    setAppliedFormSourceKey(formSourceKey);
    // Cowork 2026-09-18: "Guardar" publica el borrador; al quedar sin
    // borrador el formulario se mostraba VACÍO aunque el texto sí se guardó.
    // Sin borrador se muestra el reglamento publicado: es lo que está vigente
    // y desde ahí se sigue editando (el siguiente Guardar crea otra versión).
    if (formSourceId !== null) {
      setDraftForm({ title: formSourceTitle, content: formSourceContent });
      setSetupRuleSections(parseSetupRuleContent(formSourceContent));
    } else {
      setDraftForm(EMPTY_FORM);
      setSetupRuleSections(emptySetupRuleSections());
    }
  }

  const printHtml = useMemo(() => {
    // Decisión de Eduardo (2026-07-14): el Reglamento es el paso 2 y el
    // Formato el paso 5 — el PDF lleva SOLO los datos del torneo (paso 1) y
    // el texto que el admin escribe aquí. Nada derivado del formato: en este
    // punto las categorías ni siquiera lo tienen definido (el machote llegaba
    // a inventar reglas de Escalera para un Round Robin).
    const title = draftForm.title.trim() || currentPublished?.title || "Reglamento del torneo";
    const additionalContent = draftForm.content.trim() || currentPublished?.content || "";
    const dataItems = tournamentDataRuleItems(tournament);
    const sectionHtml = dataItems.length
      ? `<section><h3>Bases</h3><ul>${dataItems.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul></section>`
      : "";
    // Logo del organizador (identidad en Datos); fallback al póster para
    // torneos viejos que subieron su logo desde aquí cuando era logo_url.
    const logoUrl = absoluteAssetUrl(tournament?.organizer_logo_url ?? tournament?.logo_url, API_BASE_URL, window.location.origin);
    const parsedAdminSections = parseSetupRuleContent(additionalContent);
    const adminSectionHtml = SETUP_RULE_FIELDS
      .map((field) => {
        const value = parsedAdminSections[field.key].trim();
        return value ? `<section class="additional"><h3>${escapeHtml(field.label)}</h3><p>${linesToHtml(value)}</p></section>` : "";
      })
      .join("");
    const fallbackAdminHtml = additionalContent && !adminSectionHtml
      ? `<section class="additional"><h3>Reglamento adicional</h3><p>${linesToHtml(additionalContent)}</p></section>`
      : "";
    const emptyNoteHtml = !adminSectionHtml && !fallbackAdminHtml
      ? `<section><p class="empty-note">Este reglamento aún no tiene texto propio: llena los campos del paso Reglamento y vuelve a generar el PDF.</p></section>`
      : "";
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    :root{color:#111827;background:#f7f8f3;font-family:Inter,Arial,sans-serif;}
    body{margin:0;padding:28px;line-height:1.42;background:#f7f8f3;}
    .toolbar{display:flex;justify-content:flex-end;gap:10px;margin:0 auto 18px;max-width:900px;}
    .toolbar button{border:0;border-radius:999px;padding:10px 16px;background:#23420f;color:#fff;font-weight:800;cursor:pointer;}
    .toolbar button.secondary{background:#fff;color:#23420f;border:1px solid #cfdcbc;}
    .page{max-width:900px;margin:0 auto;background:#fff;border:1px solid #dfe7d5;border-radius:22px;padding:34px;box-shadow:0 18px 45px rgba(35,66,15,.14)}
    header{text-align:center;border-bottom:3px solid #8fba28;padding-bottom:20px;margin-bottom:22px;}
    .eyebrow{font-size:11px;text-transform:uppercase;letter-spacing:.18em;color:#6c8d18;font-weight:900;margin:0 0 8px;}
    h1{font-size:30px;line-height:1.08;margin:0;color:#16230f;text-transform:uppercase;}
    h2{font-size:16px;margin:10px 0 0;color:#4b5563;font-weight:700;}
    section{break-inside:avoid;border-top:1px solid #e5e7eb;padding:14px 0;}
    h3{font-size:12px;text-transform:uppercase;letter-spacing:.12em;color:#23420f;margin:0 0 7px;font-weight:900;}
    ul{margin:0;padding-left:18px;color:#1f2937;font-size:13px;}
    li{margin:0 0 6px;}
    table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px;}th,td{border:1px solid #dfe7d5;padding:8px;text-align:left;}th{background:#f8fbf1;color:#23420f;text-transform:uppercase;font-size:10px;letter-spacing:.1em;}
    .additional{background:linear-gradient(90deg,rgba(143,186,40,.12),transparent);border-radius:12px;padding:14px;margin-top:12px;border-top:0;}
    .additional p{white-space:pre-wrap;font-size:13px;margin:0;color:#1f2937;}
    .logo{display:block;max-height:64px;max-width:220px;margin:0 auto 12px;object-fit:contain;}
    .empty-note{color:#6b7280;font-size:13px;text-align:center;margin:18px 0;}
    @page{size:letter;margin:12mm;}
    @media print{body{background:#fff;padding:0}.toolbar{display:none}.page{box-shadow:none;border:0;border-radius:0;padding:0}}
  </style>
</head>
<body>
  <div class="toolbar"><button class="secondary" onclick="window.close()">Regresar</button><button onclick="window.print()">Generar PDF</button></div>
  <main class="page">
    <header>
      ${logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="Logo del organizador" />` : ""}
      <p class="eyebrow">Reglamento oficial</p>
      <h1>${escapeHtml(title)}</h1>
      <h2>${escapeHtml(tournament?.name ?? "Torneos123")}</h2>
    </header>
    ${sectionHtml}
    ${adminSectionHtml || fallbackAdminHtml}
    ${emptyNoteHtml}
  </main>
</body>
</html>`;
  }, [currentPublished, draftForm.content, draftForm.title, tournament]);

  function updateSetupRuleSection(key: SetupRuleFieldKey, value: string) {
    const next = { ...setupRuleSections, [key]: value };
    setSetupRuleSections(next);
    setDraftForm((current) => ({ ...current, content: composeSetupRuleContent(next) }));
  }

  function handlePrint() {
    const win = window.open("", "_blank");
    if (!win) {
      setError("El navegador bloqueó la ventana de PDF. Permite pop-ups para este sitio e intenta de nuevo.");
      return;
    }
    win.document.write(printHtml);
    win.document.close();
    win.focus();
  }

  function draftPayload() {
    const title = draftForm.title.trim() || "Reglamento del torneo";
    const content = draftForm.content.trim();
    if (!content) {
      setError("Llena al menos un campo del reglamento antes de guardar o publicar.");
      return null;
    }
    return { title, content };
  }

  async function saveDraftFromForm({ silent = false } = {}) {
    if (!token) return null;
    const payload = draftPayload();
    if (!payload) return null;
    setMessage(null);
    setError(null);
    setSavingDraft(true);
    try {
      const response = draft
        ? await api.rules.update(token, tournamentId, draft.id, payload)
        : await api.rules.create(token, tournamentId, payload);
      const savedRule = response.data.rule;
      setRules((current) => {
        const withoutSaved = current.filter((rule) => rule.id !== savedRule.id);
        return [savedRule, ...withoutSaved].sort((a, b) => b.version - a.version || b.id - a.id);
      });
      if (!silent) setMessage(draft ? "Borrador guardado." : "Borrador creado y guardado.");
      return savedRule;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el borrador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
      return null;
    } finally {
      setSavingDraft(false);
    }
  }

  async function ensureDraftForPdfUpload() {
    if (!token) return null;
    // Borrador, o sin borrador el reglamento publicado: el PDF es un adjunto
    // y se cuelga directo de la versión publicada (el backend ya lo permite),
    // así los jugadores lo ven de inmediato sin abrir ni publicar otra versión.
    if (pdfTarget) return pdfTarget;
    setMessage(null);
    setError(null);
    setCreating(true);
    try {
      const response = await api.rules.create(token, tournamentId, {
        title: draftForm.title.trim() || "Reglamento del torneo",
        content: draftForm.content.trim() || null,
      });
      const createdRule = response.data.rule;
      setRules((current) => [createdRule, ...current].sort((a, b) => b.version - a.version || b.id - a.id));
      setMessage("Borrador creado. Ya puedes subir el PDF propio.");
      return createdRule;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo crear el borrador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
      return null;
    } finally {
      setCreating(false);
    }
  }

  // Guardar = publicar (pedido Eduardo 2026-08-18): para el admin no existe
  // el paso "publicar" — lo que guarda ES lo que ve el jugador en el link
  // público. Por dentro se conserva el versionado (cada guardado publica una
  // versión nueva inmutable).
  async function handleSaveDraft(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    // Sin borrador y sin cambios respecto a lo publicado: no se crea otra
    // versión (una versión nueva pide a los jugadores volver a aceptar).
    if (
      !draft &&
      currentPublished &&
      draftForm.content.trim() === currentPublished.content.trim() &&
      (draftForm.title.trim() || "Reglamento del torneo") === currentPublished.title.trim()
    ) {
      setError(null);
      setMessage("Sin cambios: este ya es el reglamento que ve el jugador.");
      return;
    }
    const savedRule = await saveDraftFromForm({ silent: true });
    if (!savedRule) return;
    setBusyId(savedRule.id);
    try {
      await api.rules.publish(token, tournamentId, savedRule.id);
      setMessage("Reglamento guardado: ya es el que ve el jugador en el link público.");
      await loadRules();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "El texto se guardó pero no quedó publicado. Intenta guardar de nuevo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleUploadOriginalPdf(file: File | null) {
    if (!token || !file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("El reglamento original debe ser PDF.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("El PDF excede el límite de 20 MB.");
      return;
    }
    const targetDraft = await ensureDraftForPdfUpload();
    if (!targetDraft) return;
    setMessage(null);
    setError(null);
    setUploadingPdf(true);
    try {
      const response = await api.rules.uploadOriginalPdf(token, tournamentId, targetDraft.id, file);
      const updatedRule = response.data.rule;
      setRules((current) => [updatedRule, ...current.filter((rule) => rule.id !== updatedRule.id)].sort((a, b) => b.version - a.version || b.id - a.id));
      setMessage(
        updatedRule.status === "published"
          ? "PDF propio cargado: los jugadores ya lo ven como descarga en el reglamento publicado."
          : "PDF propio cargado. Se mostrará como descarga para jugadores cuando publiques esta versión.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo subir el PDF propio.");
    } finally {
      setUploadingPdf(false);
    }
  }

  async function handleRemoveAttachment(index: number) {
    if (!token || !pdfTarget) return;
    const item = pdfAttachments[index];
    if (!item) return;
    // En la versión publicada se quita de inmediato para los jugadores.
    const isPublished = pdfTarget.status === "published";
    const question = isPublished
      ? `¿Quitar "${item.filename || "este PDF"}" del reglamento publicado? Los jugadores dejarán de verlo.`
      : `¿Quitar "${item.filename || "este PDF"}" del reglamento?`;
    if (!(await askConfirm(question, { acceptLabel: "Sí, quitar PDF" }))) return;
    setMessage(null);
    setError(null);
    setUploadingPdf(true);
    try {
      const response = await api.rules.removeAttachment(token, tournamentId, pdfTarget.id, index);
      const updatedRule = response.data.rule;
      setRules((current) => [updatedRule, ...current.filter((rule) => rule.id !== updatedRule.id)].sort((a, b) => b.version - a.version || b.id - a.id));
      setMessage(isPublished ? "PDF quitado: los jugadores ya no lo ven." : "PDF quitado del reglamento.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo quitar el PDF.");
    } finally {
      setUploadingPdf(false);
    }
  }
  function renderAttachmentList() {
    if (pdfAttachments.length === 0) return null;
    return (
      <ul className="upload-current-list" aria-label="PDFs propios subidos">
        {pdfAttachments.map((item, index) => {
          const name = item.filename || uploadedFileName(item.url) || "Ver PDF";
          return (
            <li key={item.url} className="upload-current">
              <span className="upload-current__icon" aria-hidden="true">PDF</span>
              <a className="upload-current__name" href={item.url} target="_blank" rel="noreferrer" title={name}>
                {name}
              </a>
              <button type="button" className="btn btn--ghost btn--compact upload-current__remove" onClick={() => void handleRemoveAttachment(index)} disabled={uploadingPdf || savingDraft}>
                Quitar
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  async function handlePublish(ruleId: number) {
    if (!token) return;
    if (!(await askConfirm("¿Publicar este reglamento? Una vez publicado no se puede editar.", { acceptLabel: "Sí, publicar reglamento" }))) return;
    setBusyId(ruleId);
    setMessage(null);
    setError(null);
    try {
      await api.rules.publish(token, tournamentId, ruleId);
      setMessage("Reglamento publicado.");
      await loadRules();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo publicar.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeleteDraft(ruleId: number) {
    if (!token) return;
    if (!(await askConfirm("¿Eliminar este borrador?", { acceptLabel: "Sí, eliminar borrador", tone: "danger" }))) return;
    setBusyId(ruleId);
    setMessage(null);
    setError(null);
    try {
      await api.rules.remove(token, tournamentId, ruleId);
      setMessage("Borrador eliminado.");
      await loadRules();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyId(null);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  return (
    <div className={setupMode ? "wiz-step wiz-step--reglamento" : undefined}>
      {setupMode ? (
        <>
          <TournamentSetupProgress current={2} />
          <section className="wiz-hero wiz-hero--navy">
            <div>
              <p className="eyebrow">{tournament ? `Paso 2 de 10 · ${tournament.name}` : "Paso 2 de 10"}</p>
              <h1><span className="hint-label">Reglamento<GuideHelpButton /></span></h1>
              <p className="wiz-hero__lead">
                Captura solo las condiciones particulares del torneo. Las bases generadas por formato aparecerán después en el reglamento oficial.
              </p>
            </div>
            <div className="wiz-hero__actions">
              <button className="btn btn--ghost" type="button" onClick={handlePrint}>Ver en PDF</button>
              {draft ? (
                <button className="btn wiz-btn-lime" type="button" onClick={() => void handlePublish(draft.id)} disabled={busyId !== null}>
                  {busyId === draft.id ? "Publicando…" : "Publicar versión"}
                </button>
              ) : null}
            </div>
          </section>
          {rules.length > 0 ? (
            <div className="wiz-versions" aria-label="Versiones del reglamento">
              {rules.map((r) => (
                <article key={r.id} className={`wiz-version${r.status === "draft" ? " is-draft" : ""}`}>
                  <strong>Versión {r.version}</strong>
                  <span className={`wiz-chip wiz-chip--${r.status === "published" ? "ok" : "draft"}`}>
                    {STATUS_LABELS[r.status] ?? r.status}
                    {r.status === "published" ? ` · ${formatDateTime(r.published_at)} · inmutable` : " · editando"}
                  </span>
                </article>
              ))}
            </div>
          ) : null}
          {(error || message) && (
            <section className="card">
              {error && <p role="alert" className="form-error">{error}</p>}
              {message && <p className="form-message">{message}</p>}
            </section>
          )}
        </>
      ) : (
        <section className="card">
          <div className="card__header-row">
            <div>
              <h1 className="card__title">
                <span className="hint-label">
                  Reglamento
                  {tournament ? ` · ${tournament.name}` : ""}
                  <GuideHelpButton />
                </span>
              </h1>
              <p className="card__subtitle">
                Redacta el reglamento, publícalo cuando esté listo y revisa la
                aceptación de cada jugador.
              </p>
            </div>
            <div className="form__actions">
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/players`}>Jugadores</Link>
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/groups`}>Grupos</Link>
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/schedule`}>Calendario</Link>
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/standings`}>Standings</Link>
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/communications`}>Comunicados</Link>
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}`}>Volver al torneo</Link>
              <button className="btn btn--ghost" type="button" onClick={handlePrint}>Ver en PDF</button>
            </div>
          </div>
          {error && <p role="alert" className="form-error">{error}</p>}
          {message && <p className="form-message">{message}</p>}
        </section>
      )}

      {!setupMode && (
        <section className="card rules-generated-card">
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Reglamento generado por el sistema</h2>
            <p className="card__subtitle">
              Esta guía se arma automáticamente con el formato y fechas del
              torneo y es solo de referencia para el admin: tus jugadores
              únicamente ven el reglamento que publiques abajo.
            </p>
          </div>
        </div>
        {!loading && !currentPublished ? (
          <p className="form-error" role="alert">
            Este torneo aún no tiene reglamento publicado — tus jugadores no ven
            ninguno. Llena el "Reglamento editable" de abajo y presiona Guardar
            para publicarlo.
          </p>
        ) : null}
        <div className="rules-generated-grid">
          {generatedRules.sections.slice(0, 2).map((section) => {
            const items = section.items.filter((item) => item.trim());
            if (items.length === 0) return null;
            return (
              <article className="rules-generated-panel" key={section.key}>
                <h3>{section.title}</h3>
                <ul>
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        <div className="rules-generated-panel rules-generated-panel--full">
          <h3>{generatedRules.weeklyTitle}</h3>
          <div className="responsive-table-wrap">
            <table className="data-table rules-weekly-table">
              <thead>
                <tr>
                  <th>Semana</th>
                  <th>Patrón</th>
                </tr>
              </thead>
              <tbody>
                {generatedRules.weeklyRows.map((row) => (
                  <tr key={row.week}>
                    <td>{row.week}</td>
                    <td>
                      {row.label}
                      {row.bye && <span className="bye-badge">BYE</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="rules-generated-grid">
          {generatedRules.sections.slice(2).map((section) => {
            const items = section.items.filter((item) => item.trim());
            if (items.length === 0) return null;
            return (
              <article className="rules-generated-panel" key={section.key}>
                <h3>{section.title}</h3>
                <ul>
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        </section>
      )}

      <div className={setupMode ? "wiz-cols" : undefined}>
      <section className={setupMode ? "card wiz-card setup-rules-draft-card wiz-main" : "card"}>
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Reglamento editable</h2>
            <p className="card__subtitle">
              Llena primero los campos del reglamento. El PDF propio es opcional y el PDF generado toma el texto escrito aquí.
            </p>
          </div>
        </div>
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : (
          <form className="form" noValidate onSubmit={handleSaveDraft}>
            {/* Sin validación nativa del navegador (Cowork 2026-09-19): el título
                vacío usa el default y el contenido vacío avisa dentro de la
                página (draftPayload). */}
            <div className="form__actions" style={{ alignItems: "center" }}>
              {draft ? (
                <>
                  <span className="status-pill status-pill--dropped">
                    {STATUS_LABELS[draft.status] ?? draft.status}
                  </span>
                  <span className="kbd">v{draft.version}</span>
                  <span className="muted">
                    Última actualización · {formatDateTime(draft.updated_at)}
                  </span>
                </>
              ) : (
                <span className="muted">Escribe el reglamento y presiona Guardar: quedará visible de inmediato en el link público.</span>
              )}
              {pdfAttachments.length > 0 ? (
                <span className="muted">
                  {pdfAttachments.length} de {RULE_MAX_ATTACHMENTS} PDF{pdfAttachments.length === 1 ? "" : "s"} propio{pdfAttachments.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>
            {setupMode ? (
              <>
                <header className="wiz-card__head"><h2>Encabezado y bases</h2></header>
                <div className="setup-call-grid">
                  <label>
                    <span className="hint-label">
                      Título
                      <FieldHint>
                        Nombre del reglamento que se mostrará a jugadores y en el PDF generado.
                      </FieldHint>
                    </span>
                    <input
                      type="text"
                      value={draftForm.title}
                      onChange={(e) => setDraftForm({ ...draftForm, title: e.target.value })}
                      disabled={savingDraft}
                      placeholder="Reglamento del torneo"
                    />
                  </label>
                </div>
                <header className="wiz-card__head"><h2>Reglas del torneo</h2><p>Estos textos se guardan como reglamento editable y también aparecen en el PDF generado.</p></header>
                <div className="setup-call-grid setup-call-grid--content setup-rules-skeleton-grid">
                  {SETUP_RULE_FIELDS.map((field) => (
                    <details key={field.key} open className={`wiz-collapse${field.key === "aclaraciones" ? " setup-call-form__wide" : ""}`}>
                      <summary>{field.label}</summary>
                      <label>
                        <span className="hint-label">
                          <span className="sr-only">{field.label}</span>
                          <FieldHint>{field.hint}</FieldHint>
                        </span>
                          <textarea
                            rows={field.key === "aclaraciones" ? 4 : 3}
                            value={setupRuleSections[field.key]}
                            onChange={(e) => updateSetupRuleSection(field.key, e.target.value)}
                            disabled={savingDraft}
                            placeholder={field.placeholder}
                          />
                      </label>
                    </details>
                  ))}
                </div>
              </>
            ) : (
              <>
                <label>
                  Título
                  <input
                    type="text"
                    value={draftForm.title}
                    onChange={(e) =>
                      setDraftForm({ ...draftForm, title: e.target.value })
                    }
                    disabled={savingDraft}
                    placeholder="Reglamento del torneo"
                  />
                </label>
                <label>
                  <span className="hint-label">
                    Reglamento adicional editable
                    <FieldHint>
                      Se publica debajo de las bases generadas por el sistema. Puedes
                      escribir condiciones especiales, costos, contacto, premios o
                      aclaraciones particulares.
                    </FieldHint>
                  </span>
                  <textarea
                    rows={18}
                    value={draftForm.content}
                    onChange={(e) =>
                      setDraftForm({ ...draftForm, content: e.target.value })
                    }
                    disabled={savingDraft}
                    className="rules-editor"
                    placeholder="Texto del reglamento (Markdown soportado)."
                  />
                </label>
                <label>
                  <span className="hint-label">
                    PDF propio opcional
                    <FieldHint>
                      Sube el reglamento oficial en PDF para que el jugador pueda descargarlo con imágenes, diagramas o logos. PDF máximo 20 MB.
                    </FieldHint>
                  </span>
                  <WizUpload label="Subir PDF">
                    <input
                      type="file"
                      accept="application/pdf"
                      disabled={uploadingPdf || savingDraft || creating || pdfLimitReached}
                      onChange={(e) => { void handleUploadOriginalPdf(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                    />
                  </WizUpload>
                  {uploadingPdf && <span className="muted">Subiendo PDF…</span>}
                  {pdfLimitReached && <span className="muted">Ya están los {RULE_MAX_ATTACHMENTS} archivos; quita uno para subir otro.</span>}
                </label>
                {renderAttachmentList()}
              </>
            )}
            <div className="form__actions">
              {/* Guardar = publicar; el logo del organizador vive en Datos
                  del torneo (pedido Eduardo 2026-08-18). */}
              <button className="btn" type="submit" disabled={savingDraft || busyId !== null}>
                {savingDraft || busyId !== null ? "Guardando…" : "Guardar"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handlePrint}
                disabled={savingDraft || busyId !== null}
              >
                Ver en PDF
              </button>
            </div>
          </form>
        )}
      </section>
      {setupMode && !loading ? (
        <aside className="wiz-aside">
          <section className="card wiz-card wiz-pdf-tile">
            <header className="wiz-card__head"><h2>PDF propio (opcional)</h2><p>Sube tu reglamento oficial si ya tienes un documento con logos, imágenes o formato propio. Hasta {RULE_MAX_ATTACHMENTS} archivos; serán descargas adicionales para jugadores.</p></header>
          <div className="setup-call-grid">
            <label>
              <span className="hint-label">
                Subir PDF propio
                <FieldHint>
                  PDF máximo 20 MB, hasta {RULE_MAX_ATTACHMENTS} archivos. No reemplazan los campos de arriba; se publican como descargas adicionales. Abajo ves lo que ya subiste y puedes quitarlo.
                </FieldHint>
              </span>
              <WizUpload label="Subir PDF">
                <input
                  type="file"
                  accept="application/pdf"
                  disabled={uploadingPdf || savingDraft || creating || pdfLimitReached}
                  onChange={(e) => { void handleUploadOriginalPdf(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                />
              </WizUpload>
              {uploadingPdf && <span className="muted">Subiendo PDF…</span>}
              {pdfLimitReached && <span className="muted">Ya están los {RULE_MAX_ATTACHMENTS} archivos; quita uno para subir otro.</span>}
            </label>
            {renderAttachmentList()}
          </div>
          </section>
        </aside>
      ) : null}
      </div>

      {/* La tarjeta de aceptación del reglamento se retiró (Eduardo
          2026-08-18): el jugador acepta una sola vez desde el link público
          al inscribirse; no hay re-aceptación ni contador que vigilar. */}

      {!setupMode && (
        <section className="card">
        <div className="card__header-row">
          <h2 className="card__title">Versiones</h2>
        </div>
        {rules.length === 0 ? (
          <p className="muted">
            Aún no hay versiones del reglamento. Cuando publiques un borrador
            aparecerá aquí.
          </p>
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table rules-versions-table">
              <thead>
                <tr>
                  <th>Versión</th>
                  <th>Estado</th>
                  <th>Título</th>
                  <th>Publicado</th>
                  <th>PDF</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td>v{r.version}</td>
                    <td>
                      <span
                        className={`status-pill status-pill--${
                          r.status === "published" ? "active" : "dropped"
                        }`}
                      >
                        {STATUS_LABELS[r.status] ?? r.status}
                      </span>
                    </td>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>{formatDateTime(r.published_at)}</td>
                    <td>
                      {ruleAttachments(r).length > 0 ? (
                        <a href={ruleAttachments(r)[0].url} target="_blank" rel="noreferrer">Descargar{ruleAttachments(r).length > 1 ? ` (+${ruleAttachments(r).length - 1})` : ""}</a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        {r.status === "draft" && (
                          <>
                            <button
                              type="button"
                              className="btn btn--compact"
                              onClick={() => void handlePublish(r.id)}
                              disabled={busyId === r.id}
                            >
                              {busyId === r.id ? "Publicando…" : "Publicar"}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--compact"
                              onClick={() => void handleDeleteDraft(r.id)}
                              disabled={busyId === r.id}
                            >
                              Eliminar
                            </button>
                          </>
                        )}
                        {r.status === "published" && (
                          <span className="muted">Inmutable</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </section>
      )}
      {setupMode ? (
        <section className="setup-call-actions setup-rules-actions wiz-bottombar">
          <Link className="btn btn--ghost" to={`/admin/tournaments/${tournamentId}/edit`}>‹ Regresar</Link>
          <Link className="btn wiz-btn-lime" to={`/admin/tournaments/${tournamentId}/setup/call`}>Continuar a convocatoria</Link>
        </section>
      ) : (
        <TournamentBottomNav tournamentId={tournamentId} />
      )}
    </div>
  );
}
